import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  adminVerify,
  adminLogout,
  adminListPosts,
  adminCreatePost,
  adminUpdatePost,
  adminDeletePost,
} from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { EMPTY_POST } from "@/components/site/admin/adminTokenStorage";

export const useAdminInsights = () => {
  const { i18n } = useTranslation();
  const [auth, setAuth] = useState(null); // null = checking, false = signed out, true = signed in
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [loading, setLoading] = useState(false);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const load = async () => {
    try {
      const data = await adminListPosts();
      setPosts(data);
    } catch (e) {
      devLog.error("AdminInsights: load failed", e);
      toast.error("Could not load posts.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await adminVerify();
        setAuth(true);
        await load();
      } catch (e) {
        devLog.warn("AdminInsights: not signed in", e);
        setAuth(false);
      }
    })();
  }, []);

  const handleAuthenticated = async () => {
    setAuth(true);
    await load();
  };

  const handleLogout = async () => {
    try { await adminLogout(); } catch (e) { devLog.warn("AdminInsights: logout failed", e); }
    setAuth(false);
    setPosts([]);
    setEditing(null);
    setForm(EMPTY_POST);
  };

  const startEdit = (post) => {
    setEditing(post.id);
    setForm({ ...EMPTY_POST, ...post, cover_image: post.cover_image || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ ...EMPTY_POST, locale: i18n.resolvedLanguage || "en" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || form.content.trim().length < 10) {
      toast.error("Title, excerpt, and content (10+ chars) required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, cover_image: form.cover_image?.trim() || null };
      if (editing) {
        await adminUpdatePost(editing, payload);
      } else {
        await adminCreatePost(payload);
      }
      toast.success(editing ? "Updated." : "Created.");
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await adminDeletePost(id);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      devLog.error("AdminInsights: delete failed", e);
      toast.error("Delete failed.");
    }
  };

  const togglePublish = async (post) => {
    try {
      await adminUpdatePost(post.id, { published: !post.published });
      await load();
    } catch (e) {
      devLog.error("AdminInsights: toggle publish failed", e);
      toast.error("Update failed.");
    }
  };

  return {
    auth,
    posts,
    editing,
    form,
    loading,
    setField,
    handleAuthenticated,
    handleLogout,
    startEdit,
    resetForm,
    handleSubmit,
    handleDelete,
    togglePublish,
    reload: load,
  };
};
